import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

// Web Audio API procedural sound engine for Xiangqi.
// Zero network requests, 0 latency, fully offline-capable and crystal clear.

class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;
  private volume: number = 0.8;

  constructor() {
    try {
      const savedEnabled = localStorage.getItem('xq_sfx_enabled');
      if (savedEnabled !== null) this.enabled = savedEnabled === 'true';
      const savedVol = localStorage.getItem('xq_sfx_volume');
      if (savedVol !== null) this.volume = parseFloat(savedVol);
    } catch {
      // ignore
    }
  }

  private async triggerHaptic(type: 'move' | 'capture' | 'check' | 'victory' | 'defeat'): Promise<void> {
    try {
      if (type === 'move') {
        await Haptics.impact({ style: ImpactStyle.Light });
      } else if (type === 'capture') {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } else if (type === 'check') {
        await Haptics.notification({ type: NotificationType.Warning });
      } else if (type === 'victory') {
        await Haptics.notification({ type: NotificationType.Success });
      } else if (type === 'defeat') {
        await Haptics.notification({ type: NotificationType.Error });
      }
    } catch {
      // Graceful fallback when running in desktop browser or when haptics unavailable
    }
  }

  private initCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(val: boolean): void {
    this.enabled = val;
    try {
      localStorage.setItem('xq_sfx_enabled', String(val));
    } catch {
      // ignore
    }
  }

  getVolume(): number {
    return this.volume;
  }

  setVolume(val: number): void {
    this.volume = Math.max(0, Math.min(1, val));
    try {
      localStorage.setItem('xq_sfx_volume', String(this.volume));
    } catch {
      // ignore
    }
  }

  /**
   * Sound 1: Wooden piece move click (thock / clack).
   */
  playMove(): void {
    void this.triggerHaptic('move');
    if (!this.enabled || this.volume <= 0) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(70, t + 0.06);

    gain.gain.setValueAtTime(0.7 * this.volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.07);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(t);
    osc.stop(t + 0.08);

    const click = ctx.createOscillator();
    const clickGain = ctx.createGain();
    click.type = 'sine';
    click.frequency.setValueAtTime(800, t);
    click.frequency.exponentialRampToValueAtTime(120, t + 0.02);

    clickGain.gain.setValueAtTime(0.4 * this.volume, t);
    clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);

    click.connect(clickGain);
    clickGain.connect(ctx.destination);

    click.start(t);
    click.stop(t + 0.04);
  }

  /**
   * Sound 2: Piece capture strike (deeper, heavier impact).
   */
  playCapture(): void {
    void this.triggerHaptic('capture');
    if (!this.enabled || this.volume <= 0) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(320, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.12);

    gain.gain.setValueAtTime(0.85 * this.volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(t);
    osc.stop(t + 0.15);
  }

  /**
   * Sound 3: Check alarm (Chiếu tướng) — distinct two-tone alert.
   */
  playCheck(): void {
    void this.triggerHaptic('check');
    if (!this.enabled || this.volume <= 0) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const t = ctx.currentTime;
    [0, 0.09].forEach((offset, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(idx === 0 ? 587.33 : 880, t + offset);
      gain.gain.setValueAtTime(0.6 * this.volume, t + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t + offset);
      osc.stop(t + offset + 0.2);
    });
  }

  /**
   * Sound 4: Victory fanfare.
   */
  playVictory(): void {
    void this.triggerHaptic('victory');
    if (!this.enabled || this.volume <= 0) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const notes = [523.25, 659.25, 783.99, 1046.5];
    const t = ctx.currentTime;
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t + idx * 0.1);
      gain.gain.setValueAtTime(0.5 * this.volume, t + idx * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, t + idx * 0.1 + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t + idx * 0.1);
      osc.stop(t + idx * 0.1 + 0.4);
    });
  }

  /**
   * Sound 5: Defeat chime.
   */
  playDefeat(): void {
    void this.triggerHaptic('defeat');
    if (!this.enabled || this.volume <= 0) return;
    const ctx = this.initCtx();
    if (!ctx) return;

    const notes = [440, 392, 349.23, 293.66];
    const t = ctx.currentTime;
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t + idx * 0.12);
      gain.gain.setValueAtTime(0.4 * this.volume, t + idx * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, t + idx * 0.12 + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t + idx * 0.12);
      osc.stop(t + idx * 0.12 + 0.35);
    });
  }
}

export const sfx = new SoundManager();
